<template>
  <div class="switcher">
    <button
      class="theme-toggle"
      :class="{ 'is-dark': isDark }"
      @click="toggleTheme"
      :aria-label="isDark ? $t('nav.themeToLight') : $t('nav.themeToDark')"
      type="button"
    >
      <span class="theme-toggle__thumb">
        <svg v-if="isDark" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
        </svg>
        <svg v-else aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="5"></circle>
          <line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
          <line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
        </svg>
      </span>
    </button>

    <select v-model="currentLocale" @change="changeLanguage" class="lang-select" :aria-label="$t('nav.language')">
      <option value="en">EN</option>
      <option value="nl">NL</option>
      <option value="es">ES</option>
    </select>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n/dist/vue-i18n.esm-bundler.js';
import i18n from '@/i18n';

const isDark = ref(false);
const { locale } = useI18n();
const currentLocale = ref('nl');

const applyTheme = (dark) => {
  document.body.classList.toggle('dark-mode', dark);
  document.querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', dark ? '#1a0033' : '#fbf7ec');
};

// Op iPhone en iPad bleef na het wisselen de achtergrond van de hero in het
// oude thema staan, terwijl de tekst erin al wel omging (witte kop op crème).
// Pas na ver weg en terug scrollen klopte het. iOS tekent de hero door zijn
// masker en de draaiende strepen in een eigen laag en hergebruikt dan de oude
// achtergrond erachter. Daarom gaan animaties, overgangen en maskers een frame
// uit (zie .theme-switching in styles.css): iOS bouwt de lagen opnieuw op en
// tekent alles in het nieuwe thema. Daarna lopen de animaties gewoon door.
// Op de Mac, ook in WebKit, was het niet na te bootsen.
const switchTheme = (dark) => {
  const root = document.documentElement;
  root.classList.add('theme-switching');
  applyTheme(dark);
  void document.body.offsetHeight;
  requestAnimationFrame(() => requestAnimationFrame(() => {
    root.classList.remove('theme-switching');
  }));
};

const toggleTheme = () => {
  isDark.value = !isDark.value;
  switchTheme(isDark.value);
  localStorage.setItem('theme', isDark.value ? 'dark' : 'light');
};

onMounted(() => {
  // Light is the default; only dark if explicitly stored.
  isDark.value = localStorage.getItem('theme') === 'dark';
  applyTheme(isDark.value);

  const storedLang = localStorage.getItem('language') || 'nl';
  locale.value = storedLang;
  i18n.global.locale = storedLang;
  currentLocale.value = storedLang;
});

const changeLanguage = () => {
  const newLocale = currentLocale.value;
  locale.value = newLocale;
  i18n.global.locale = newLocale;
  localStorage.setItem('language', newLocale);
  window.dispatchEvent(new CustomEvent('language-changed', { detail: { locale: newLocale } }));
};
</script>

<style scoped>
.switcher {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

/* Schakelaar en taalkeuze zijn allebei 36px hoog, zodat ze als paar op een
   lijn staan. Het bolletje vult de binnenkant op 2px na aan elke kant. */
.theme-toggle {
  position: relative;
  width: 62px;
  height: 36px;
  border-radius: 999px;
  border: 2px solid var(--ink);
  background: var(--surface-2);
  cursor: pointer;
  padding: 0;
  transition: background 0.3s ease;
}
.theme-toggle__thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: #fff;
  background: var(--color-primary);
  border: 2px solid var(--ink);
  transition: transform 0.25s ease-out;
}
body.dark-mode .theme-toggle__thumb { color: #1a0033; background: var(--color-accent); }
.theme-toggle.is-dark .theme-toggle__thumb { transform: translateX(26px); }
/* Onzichtbaar groter tapgebied: de schakelaar oogt 36px hoog, maar een
   vinger heeft 44px nodig. Zo blijft het uiterlijk gelijk. */
.theme-toggle::before {
  content: "";
  position: absolute;
  inset: -6px -2px;
}

.lang-select {
  width: auto;
  min-height: 36px;
  padding: 0.3rem 0.75rem;
  font-family: var(--font-display);
  /* Vet en iets groter: dun en klein oogde de taalcode grijs. */
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  border-radius: 999px;
  border: 2px solid var(--ink);
  background: var(--surface-2);
  color: var(--text);
  cursor: pointer;
}
.lang-select:focus { box-shadow: 3px 3px 0 var(--color-secondary); }

/* Op een touchscherm, ook een iPad met de volledige menubalk, het taalmenu
   op 44px: een select kan geen onzichtbaar groter tapgebied krijgen. */
@media (pointer: coarse) {
  .lang-select { min-height: 44px; padding: 0.4rem 0.8rem; }
}

@media (prefers-reduced-motion: reduce) {
  .theme-toggle,
  .theme-toggle__thumb { transition: none; }
}
</style>
